import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { turnstileWidget, type TurnstileWidget } from '../lib/turnstile.ts';
import { regisztracioApi, type HibaKod } from '../regisztracio/api.ts';
import { jogiAllapot, KIEMELT_FELTETELEK } from '../regisztracio/jogi.ts';
import {
  elfogadasokUresen, HIBA_SZOVEGEK, nagyHalozat, regisztracioHibak, regisztracioKeres, regisztracioUrlapUres,
  type Elfogadasok, type Halozat, type RegisztracioUrlapAdat,
} from '../regisztracio/urlap.ts';
import { Gomb, Mezo, Valaszto } from '../ui/elemek.tsx';
import BelepesKeret from './BelepesKeret.tsx';

// Az iroda-regisztráció kapcsolója: alapból ki van kapcsolva (a háttér is 503-at ad).
const REGISZTRACIO_BE = import.meta.env.VITE_REGISZTRACIO === 'be';
const TURNSTILE_KULCS = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
// A háttér ugyanezt az actiont ellenőrzi (supabase/functions/_shared/regisztracio.ts).
const TURNSTILE_ACTION = 'iroda_regisztracio';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;

const REGIO_JAVASLAT = ['Budapest', 'Pest vármegye'];
const HALOZAT_NEV: Record<Halozat, string> = { duna_house: 'Duna House', otthon_centrum: 'Otthon Centrum', egyeb: 'Egyéb hálózat', fuggetlen: 'Független iroda' };

function Jelolo({ checked, onChange, hiba, children }: { checked: boolean; onChange(be: boolean): void; hiba?: string; children: ReactNode }) {
  const id = useId();
  const hibaId = `${id}-hiba`;
  return (
    <div className="grid gap-1">
      <div className="flex items-start gap-3">
        <input id={id} type="checkbox" className="mt-0.5 size-5 shrink-0 accent-kiemelt" checked={checked}
          aria-invalid={hiba ? true : undefined} aria-describedby={hiba ? hibaId : undefined}
          onChange={(e) => onChange(e.target.checked)} />
        <label htmlFor={id} className="min-h-6 text-sm leading-6">{children}</label>
      </div>
      {hiba && <p id={hibaId} className="pl-8 text-sm text-hiba">{hiba}</p>}
    </div>
  );
}

function Szakasz({ cim, leiras, children }: { cim: string; leiras?: string; children: ReactNode }) {
  return (
    <fieldset className="grid min-w-0 gap-4 border-0 p-0">
      <legend className="mb-1 text-lg font-medium tracking-[-0.015em]">{cim}</legend>
      {leiras && <p className="-mt-2 text-sm text-szoveg-halk">{leiras}</p>}
      {children}
    </fieldset>
  );
}

const Link_ = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noopener" className="text-kiemelt underline">{children}</a>
);

export default function Regisztracio() {
  const jogi = useMemo(jogiAllapot, []);
  const api = useMemo(() => regisztracioApi(SUPABASE_URL ?? ''), []);
  const [adat, setAdat] = useState<RegisztracioUrlapAdat>(regisztracioUrlapUres);
  const [elfogadas, setElfogadas] = useState<Elfogadasok>(elfogadasokUresen);
  const [probalt, setProbalt] = useState(false);
  const [kuldes, setKuldes] = useState(false);
  const [szerverHiba, setSzerverHiba] = useState<HibaKod | null>(null);
  const [sikerEmail, setSikerEmail] = useState<string | null>(null);
  const urlapRef = useRef<HTMLFormElement>(null);
  const tsHelyRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<TurnstileWidget | null>(null);
  const sikerCimRef = useRef<HTMLHeadingElement>(null);

  const hibak = regisztracioHibak(adat, elfogadas);
  const mutat = (k: keyof RegisztracioUrlapAdat) => (v: string) => setAdat((a) => ({ ...a, [k]: v }));
  const szoveges = (k: keyof RegisztracioUrlapAdat) => ({
    name: k, value: adat[k], onChange: (e: { target: { value: string } }) => mutat(k)(e.target.value), hiba: probalt ? hibak[k] : undefined,
  });

  useEffect(() => {
    if (!REGISZTRACIO_BE || sikerEmail !== null) return;
    let megszunt = false;
    let widget: TurnstileWidget | null = null;
    if (!TURNSTILE_KULCS || !tsHelyRef.current) { setSzerverHiba('turnstile'); return; }
    turnstileWidget(tsHelyRef.current, TURNSTILE_KULCS, TURNSTILE_ACTION)
      .then((w) => { if (megszunt) w.eltavolit(); else { widget = w; widgetRef.current = w; } })
      // A betöltés hibája látható üzenetet kap; a bekuldés ilyenkor úgysem járna sikerrel.
      .catch(() => { if (!megszunt) setSzerverHiba('turnstile'); });
    return () => { megszunt = true; widget?.eltavolit(); widgetRef.current = null; };
  }, [sikerEmail]);

  useEffect(() => { if (sikerEmail !== null) sikerCimRef.current?.focus(); }, [sikerEmail]);

  async function bekuld(e: FormEvent) {
    e.preventDefault();
    setProbalt(true);
    setSzerverHiba(null);
    if (Object.keys(regisztracioHibak(adat, elfogadas)).length > 0) {
      // A hibás mezők megjelenése után az elsőre ugrunk (billentyűzetes és képernyőolvasós használathoz).
      setTimeout(() => urlapRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(), 0);
      return;
    }
    setKuldes(true);
    let token: string;
    try {
      if (!widgetRef.current) throw new Error('turnstile_nincs');
      token = await widgetRef.current.token();
    } catch {
      setKuldes(false);
      setSzerverHiba('turnstile');
      widgetRef.current?.reset();
      return;
    }
    const r = await api.bekuldes(regisztracioKeres(adat, token, jogi));
    setKuldes(false);
    if (r.ok) { setSikerEmail(adat.kapcsolattartoEmail.trim()); return; }
    setSzerverHiba(r.hiba);
  }

  if (!REGISZTRACIO_BE) {
    return (
      <BelepesKeret cim="Iroda regisztrációja" szeles>
        <p>{HIBA_SZOVEGEK.kikapcsolva}</p>
        <Link to="/belepes" className="text-kiemelt underline">Vissza a belépéshez</Link>
      </BelepesKeret>
    );
  }

  if (sikerEmail !== null) {
    return (
      <BelepesKeret cim="Köszönjük a regisztrációt" szeles>
        <h2 ref={sikerCimRef} tabIndex={-1} className="sr-only">A regisztráció elküldve</h2>
        <p role="status" className="rounded-xl border border-kiemelt bg-kiemelt-halvany px-3.5 py-2.5 text-sm">
          Elküldtük a megerősítő linket a(z) {sikerEmail} címre. A link 48 óráig érvényes. A regisztrációját a megerősítés után bíráljuk el; erről e-mailben értesítjük.
        </p>
        <Link to="/belepes" className="text-kiemelt underline">Vissza a belépéshez</Link>
      </BelepesKeret>
    );
  }

  const halozat = adat.halozat;
  return (
    <BelepesKeret cim="Iroda regisztrációja" szeles>
      <p className="text-szoveg-halk">
        Itt jelentkezhet az iroda a platform használatára. A regisztráció után e-mailben megerősítjük a kapcsolattartó címét, majd a
        jelentkezést elbíráljuk. Hozzáférést csak a jóváhagyás után kap, a belépéshez ekkor meghívót küldünk. Az űrlap minden mezője
        kötelező, ahol nincs másképp jelölve.
      </p>
      <form ref={urlapRef} onSubmit={(e) => void bekuld(e)} noValidate className="grid gap-8">
        <Szakasz cim="Iroda">
          <Mezo label="Az iroda megjelenő neve" autoComplete="organization" maxLength={120} {...szoveges('irodaNev')} />
          <Valaszto label="Hálózat" value={halozat} name="halozat" hiba={probalt ? hibak.halozat : undefined}
            onChange={(e) => setAdat((a) => ({ ...a, halozat: e.target.value as Halozat | '' }))}>
            <option value="">Válasszon…</option>
            {(Object.keys(HALOZAT_NEV) as Halozat[]).map((h) => <option key={h} value={h}>{HALOZAT_NEV[h]}</option>)}
          </Valaszto>
          {halozat === 'egyeb' && (
            <>
              <Mezo label="A hálózat neve" maxLength={120} {...szoveges('halozatEgyeb')} />
              <p className="-mt-2 text-sm text-szoveg-halk">Más hálózat logója csak a hálózat írásos engedélyével jelenhet meg.</p>
            </>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Mezo label="Település" autoComplete="address-level2" maxLength={120} {...szoveges('telepules')} />
            <div className="grid">
              <Mezo label="Régió" list="regio-javaslat" maxLength={120} {...szoveges('regio')} />
              <datalist id="regio-javaslat">{REGIO_JAVASLAT.map((r) => <option key={r} value={r} />)}</datalist>
            </div>
          </div>
          <Mezo label="Utca és házszám (nem kötelező)" autoComplete="street-address" maxLength={200} {...szoveges('irodaCim')} />
        </Szakasz>

        <Szakasz cim="Cég" leiras="A számlázáshoz és a szerződéshez használjuk.">
          <Valaszto label="Cégforma" value={adat.cegForma} name="cegForma"
            onChange={(e) => setAdat((a) => ({ ...a, cegForma: e.target.value === 'egyeni_vallalkozo' ? 'egyeni_vallalkozo' : 'tarsasag' }))}>
            <option value="tarsasag">Társaság (cégjegyzékszámmal)</option>
            <option value="egyeni_vallalkozo">Egyéni vállalkozó</option>
          </Valaszto>
          <Mezo label="Cégnév" maxLength={200} {...szoveges('cegnev')} />
          <Mezo label="Székhely" autoComplete="off" maxLength={300} {...szoveges('szekhely')} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Mezo label="Adószám" inputMode="numeric" placeholder="12345678-1-23" maxLength={20} {...szoveges('adoszam')} />
            {adat.cegForma === 'tarsasag'
              ? <Mezo label="Cégjegyzékszám" inputMode="numeric" placeholder="01-09-123456" maxLength={20} {...szoveges('cegjegyzekszam')} />
              : <Mezo label="Egyéni vállalkozói nyilvántartási szám" inputMode="numeric" maxLength={20} {...szoveges('evNyilvantartasiSzam')} />}
          </div>
          <Mezo label="Számlázási e-mail-cím" type="email" inputMode="email" autoComplete="off" maxLength={200} {...szoveges('szamlazasiEmail')} />
        </Szakasz>

        <Szakasz cim="Kapcsolattartó" leiras="Ő lesz az iroda vezetője a felületen. A jóváhagyás után ide küldjük a meghívót.">
          <Mezo label="Név" autoComplete="name" maxLength={120} {...szoveges('kapcsolattartoNev')} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Mezo label="E-mail-cím" type="email" inputMode="email" autoComplete="email" maxLength={200} {...szoveges('kapcsolattartoEmail')} />
            <Mezo label="Mobilszám" type="tel" inputMode="tel" autoComplete="tel" placeholder="+36 30 123 4567" maxLength={30} {...szoveges('kapcsolattartoTelefon')} />
          </div>
          <Mezo label="Megjegyzés (nem kötelező)" multiline maxLength={1000} {...szoveges('megjegyzes')} />
        </Szakasz>

        <Szakasz cim="Feltételek és nyilatkozatok">
          <div className="grid gap-2 rounded-xl border border-vonal bg-felulet px-4 py-3.5 text-sm">
            <p>
              A Partner ÁSZF 13.5. pontja alapján, a Ptk. 6:78. § (2) bekezdése szerint külön felhívjuk a figyelmét a szokásos szerződési
              gyakorlattól eltérő, illetve az Ön terhére szóló alábbi feltételekre:
            </p>
            <ul className="list-disc pl-5">
              {KIEMELT_FELTETELEK.map((f) => <li key={f}>{f}</li>)}
            </ul>
          </div>
          <div className="grid gap-4">
            <Jelolo checked={elfogadas.aszf} onChange={(v) => setElfogadas((e) => ({ ...e, aszf: v }))} hiba={probalt ? hibak.aszf : undefined}>
              Elolvastam és elfogadom a <Link_ href={jogi.aszf.url}>Partner ÁSZF</Link_> {jogi.aszf.verzio} verzióját, benne a 8. pont szerinti
              adatfeldolgozói megállapodást, és kifejezetten elfogadom a fent kiemelt feltételeket.
            </Jelolo>
            <Jelolo checked={elfogadas.adatvedelem} onChange={(v) => setElfogadas((e) => ({ ...e, adatvedelem: v }))} hiba={probalt ? hibak.adatvedelem : undefined}>
              Megismertem az <Link_ href={jogi.adatvedelem.url}>Adatvédelmi tájékoztatót</Link_> ({jogi.adatvedelem.verzio} verzió).
            </Jelolo>
            <Jelolo checked={elfogadas.kepviselet} onChange={(v) => setElfogadas((e) => ({ ...e, kepviselet: v }))} hiba={probalt ? hibak.kepviselet : undefined}>
              Kijelentem, hogy jogosult vagyok a megadott céget képviselni, vagy a képviselőjétől erre felhatalmazást kaptam.
            </Jelolo>
            {nagyHalozat(halozat) && (
              <Jelolo checked={elfogadas.halozati} onChange={(v) => setElfogadas((e) => ({ ...e, halozati: v }))} hiba={probalt ? hibak.halozati : undefined}>
                Kijelentem, hogy az iroda a(z) {halozat === '' ? '' : HALOZAT_NEV[halozat]} hálózat tagja.
              </Jelolo>
            )}
          </div>
          <p className="text-sm text-szoveg-halk">
            A regisztráció jelentkezés, nem szerződéskötés. A díjakat és az egyedi feltételeket az Egyedi Szerződés tartalmazza.
          </p>
        </Szakasz>

        <div ref={tsHelyRef} />

        {probalt && Object.keys(hibak).length > 0 && szerverHiba === null && (
          <p role="alert" className="rounded-xl border border-hiba bg-hiba-hatter px-3.5 py-2.5 text-sm text-hiba">
            Néhány mező hiányos vagy hibás. Kérjük, javítsa a jelölt mezőket.
          </p>
        )}
        {szerverHiba && (
          <div role="alert" className="grid gap-2 rounded-xl border border-hiba bg-hiba-hatter px-3.5 py-2.5 text-sm text-hiba">
            <p>{HIBA_SZOVEGEK[szerverHiba]}</p>
            {(szerverHiba === 'jogi_verzio' || szerverHiba === 'turnstile') && (
              <button type="button" className="justify-self-start underline" onClick={() => window.location.reload()}>Az oldal újratöltése</button>
            )}
          </div>
        )}
        <div className="grid gap-3">
          <Gomb type="submit" toltes={kuldes} disabled={szerverHiba === 'jogi_verzio'}>Regisztráció beküldése</Gomb>
          <Link to="/belepes" className="text-sm text-kiemelt underline">Már van fiókom, belépek</Link>
        </div>
      </form>
    </BelepesKeret>
  );
}
